"use client";

import { Loader2, MinusIcon, PlusIcon, ShoppingCart, Trash2Icon } from "lucide-react";
import { useEffect, useState } from "react";
import Image from "next/image";
import { Button } from "@/components/ui/button";
import { Button as AtomButton } from "@/components/atoms/buttons";
import { __, _x } from "@/lib/i18n/functions";
import { Locales } from "@/lib/i18n";
import { useCart } from "@/hooks/useCart";
import { useAddToCart, useRemoveCartItem, useUpdateCartItem } from "@/hooks/useCartMutations";
import { Toast, toast } from "@/components/atoms/toast";
import ProductBadge from "@/components/atoms/product-badge";
import { formatProductQuantity } from "@/lib/product-quantity-label";
import { formatPrice, isSaleProduct } from "@/lib/utils";
import { useWishlist } from "@/hooks/useWishlist";
import ProductConfigurationModal from "@/components/modules/account/product-configuration-modal";
import { ProductDocument } from "@/prismicio-types";
import { StoreProduct } from "@medusajs/types";
import { Badge } from "@/components/ui/badge";
import { PriceToastDropdown } from "../atoms/price-toast-dropdown";
import { useProductVariant } from "@/hooks/useProductVariant";
import {
  getCartQuantityStep,
  normalizeConditioning,
  toLotQuantity,
  normalizePieceQuantity,
  toPiecePrice,
} from "@/lib/product-units";

type CartProductCardProps = {
  productName: string;
  productPrice: string;
  productImage: string;
  alt: string;
  locale: Locales;
  isLoading?: boolean;
  medusaVariantId: string;
  onQuantityChange?: (quantity: number) => void;
  productConditioning: number | string;
  price: number;
  variant?: "default" | "wishlist";
  productId?: string;
  isFullImage?: boolean;
  isOutOfStock?: boolean;
  isRange?: boolean;
  prismicProduct?: ProductDocument;
  medusaProduct?: StoreProduct;
  isPriceDynamic?: boolean;
};

const CartProductCard = ({
  productName,
  alt,
  productPrice,
  productImage,
  locale,
  isLoading,
  medusaVariantId,
  onQuantityChange,
  productConditioning,
  price,
  variant = "default",
  productId,
  isFullImage = false,
  isOutOfStock = false,
  isRange = false,
  prismicProduct,
  medusaProduct,
  isPriceDynamic = false,
}: CartProductCardProps) => {
  const { cart } = useCart();
  const quantityStep = getCartQuantityStep(productConditioning);
  const physicalPieceCount = normalizeConditioning(productConditioning);

  // ── Variant resolution — single source of truth ───────────────────────────
  // No URL seeding here: this component renders in wishlist (mobile) and cart
  // contexts where the URL ?surface= param is either absent or irrelevant.
  // The hook reads: store → cart → surface_min (in that priority).
  // medusaVariantId is already the resolved variant id when passed from
  // WishlistProductCard, so for single-variant products it's always correct.
  const { hasMultipleVariants, selectedVariant, selectedVariantPrice, hasCustomization } = useProductVariant(
    medusaProduct,
    medusaProduct?.id || ""
  );

  // ── Cart item detection ───────────────────────────────────────────────────
  // For multi-variant: match the SPECIFIC selected variant, not any variant.
  // This ensures isInCart=false when a different surface is in the cart.
  // For single-variant: medusaVariantId (already resolved by the parent).
  const cartItem = hasMultipleVariants
    ? cart?.items?.find((item) => item.variant_id === selectedVariant?.id)
    : cart?.items?.find((item) => item.variant_id === medusaVariantId);

  const cartQuantity = toLotQuantity(cartItem?.quantity || 0, productConditioning).toString();

  // ── Wishlist helpers ──────────────────────────────────────────────────────
  const { activeListId, removeFromList, updateQuantity: updateWishlistQuantity, lists } = useWishlist();
  const activeList = lists?.find((list) => list.id === activeListId);
  const wishlistItem = activeList?.products?.find((p) => p.productId === productId);
  const initialWishlistQuantity = wishlistItem?.quantity || 1;

  // ── isInCart ──────────────────────────────────────────────────────────────
  const isInCart =
    variant === "wishlist"
      ? Boolean(
          cartItem &&
          Array.isArray(cartItem.metadata?.wishlist_ids) &&
          cartItem.metadata.wishlist_ids.includes(activeListId)
        )
      : Boolean(cartItem);

  // ── Quantity state ────────────────────────────────────────────────────────
  const [count, setCount] = useState(() => {
    if (isInCart && cartItem?.quantity) return cartItem.quantity;
    if (variant === "wishlist") return normalizePieceQuantity(initialWishlistQuantity, quantityStep);
    return quantityStep;
  });

  useEffect(() => {
    if (cartItem?.quantity !== undefined) setCount(cartItem.quantity);
  }, [cartItem?.quantity]);

  useEffect(() => {
    const normalizedInitialQuantity = normalizePieceQuantity(initialWishlistQuantity, quantityStep);
    if (!isInCart && normalizedInitialQuantity !== count) setCount(normalizedInitialQuantity);
  }, [count, initialWishlistQuantity, isInCart, quantityStep]);

  useEffect(() => {
    onQuantityChange?.(count);
  }, [count, onQuantityChange]);

  // ── Mutations ─────────────────────────────────────────────────────────────
  const { addToCart, isLoading: isAddingToCart } = useAddToCart({
    onSuccess: () => toast.success({ title: __("Item added to cart!", locale) }),
    onError: (error) => {
      console.error("Failed to add to cart:", error);
      toast.error({ title: __("Failed to add item to cart", locale) });
    },
  });

  const { removeItem, isLoading: isRemoving } = useRemoveCartItem({
    onError: (error) => {
      console.error("Error removing item from cart:", error, cartItem);
      toast.error({ title: __("Error removing item from cart", locale) });
    },
  });

  const { updateQuantity } = useUpdateCartItem({
    onError: (err) => {
      console.error("Error updating quantity", err);
      toast.error({ title: __("Failed to update quantity", locale) });
    },
  });

  // ── Quantity handlers ─────────────────────────────────────────────────────
  const handleQuantityChange = (newQuantity: number) => {
    if (cartItem?.id && isInCart && newQuantity !== cartItem.quantity) {
      if (variant === "wishlist" && activeListId) {
        updateQuantity(cartItem.id, newQuantity, {
          wishlist_ids: (cartItem.metadata?.wishlist_ids as string[]) || [activeListId],
        });
      } else {
        updateQuantity(cartItem.id, newQuantity);
      }
      return;
    }
    if (wishlistItem?.id && activeListId && !isInCart && variant === "wishlist") {
      updateWishlistQuantity(activeListId, wishlistItem.id, newQuantity);
    }
  };

  const handleDecrease = () => {
    setCount((prev) => {
      const next = prev > quantityStep ? prev - quantityStep : prev;
      handleQuantityChange(next);
      return next;
    });
  };

  const handleIncrease = () => {
    setCount((prev) => {
      const next = prev + quantityStep;
      handleQuantityChange(next);
      return next;
    });
  };

  // ── Add to cart ───────────────────────────────────────────────────────────
  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isRange && !hasCustomization) {
      toast.error({ title: __("Please customize this product before adding to cart.", locale) });
      return;
    }

    // ── Multi-variant path ─────────────────────────────────────────────────
    if (hasMultipleVariants && selectedVariant) {
      const allVariantIds = medusaProduct?.variants?.map((v) => v.id) ?? [];
      const existingSelectedItem = cart?.items?.find((i) => i.variant_id === selectedVariant.id);
      const existingOtherItem = cart?.items?.find(
        (i) => i.variant_id !== selectedVariant.id && allVariantIds.includes(i.variant_id ?? "")
      );

      if (existingSelectedItem) {
        const existing = Array.isArray(existingSelectedItem.metadata?.wishlist_ids)
          ? (existingSelectedItem.metadata.wishlist_ids as string[])
          : [];
        const merged =
          variant === "wishlist" && activeListId ? Array.from(new Set([...existing, activeListId])) : existing;
        updateQuantity(
          existingSelectedItem.id,
          existingSelectedItem.quantity + count,
          variant === "wishlist" ? { wishlist_ids: merged } : undefined
        );
        toast.success({ title: __("Item added to cart!", locale) });
        return;
      }

      if (existingOtherItem) removeItem(existingOtherItem.id);

      addToCart(
        selectedVariant.id,
        count,
        variant === "wishlist" && activeListId ? { wishlist_ids: [activeListId] } : undefined
      );
      return;
    }

    // ── Single-variant path ────────────────────────────────────────────────
    if (variant === "wishlist") {
      const existingCartItem = cart?.items?.find((i) => i.variant_id === medusaVariantId);
      if (existingCartItem) {
        const existing = Array.isArray(existingCartItem.metadata?.wishlist_ids)
          ? existingCartItem.metadata.wishlist_ids
          : [];
        const merged = existing.includes(activeListId ?? "") ? existing : [...existing, activeListId];
        updateQuantity(existingCartItem.id, existingCartItem.quantity + count, {
          wishlist_ids: merged,
        });
        toast.success({ title: __("Item added to cart!", locale) });
      } else {
        addToCart(medusaVariantId, count, { wishlist_ids: [activeListId || ""] });
      }
    } else {
      addToCart(medusaVariantId, count);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = () => {
    const isInWishlist = activeListId && productId && wishlistItem?.id && variant === "wishlist";

    if (isInWishlist && isInCart) {
      const originalQty = wishlistItem.quantity || 1;
      const inOtherLists = lists?.some(
        (list) => list.id !== activeListId && list.products?.some((p) => p.productId === productId)
      );
      if (inOtherLists) {
        updateWishlistQuantity(activeListId, wishlistItem.id, originalQty);
        setCount(originalQty);
      } else {
        updateWishlistQuantity(activeListId, wishlistItem.id, cartItem?.quantity || 1);
        setCount(cartItem?.quantity || 1);
      }
      removeItem(cartItem?.id || "");
      toast.success({ title: __("Item removed from cart!", locale) });
      return;
    }

    if (isInWishlist && !isInCart) {
      removeFromList(activeListId!, productId!);
      toast.success({ title: __("Product removed from wishlist", locale) });
      return;
    }

    if (isInCart) {
      removeItem(cartItem?.id || "");
      toast.success({ title: __("Item removed from cart!", locale) });
    }
  };

  const pricePerPiece = toPiecePrice(price, productConditioning);

  // resolvedDisplayPrice: for multi-variant use selectedVariantPrice from hook,
  // for single-variant use the prop (already formatted by the parent).
  const resolvedDisplayPrice = hasMultipleVariants ? selectedVariantPrice : productPrice;

  return (
    <article className="flex w-full items-center gap-[23px]" aria-label={__("Product item", locale)}>
      <figure className="bg-product-card relative flex size-[6.875rem] shrink-0 items-center justify-center overflow-hidden rounded-lg border-none">
        {productImage &&
          (isFullImage ? (
            <Image className="absolute object-cover mix-blend-multiply" alt={alt || ""} fill src={productImage} />
          ) : (
            <div className="relative size-20">
              <Image className="absolute object-contain mix-blend-multiply" alt={alt || ""} fill src={productImage} />
            </div>
          ))}
        {isSaleProduct(medusaProduct) && (
          <Badge className="absolute top-2 -left-6 w-[90px] -rotate-45 rounded-none" variant="festi">
            {__("To sell", locale)}
          </Badge>
        )}
      </figure>

      <div className="flex w-full flex-col items-start justify-center gap-1">
        <div className="flex w-full flex-col items-start justify-center gap-1">
          {productName && (
            <h5 className="text-blue-dark w-full text-lg leading-5 font-normal break-words whitespace-pre-wrap max-sm:text-base">
              {productName}
            </h5>
          )}

          {/* ── Range / dynamic price product ─────────────────────────── */}
          {(isRange || isPriceDynamic) && (
            <div className="flex w-full items-center justify-between gap-1">
              {hasMultipleVariants ? (
                <p className="font-cabinet-grotesk text-base font-bold">{resolvedDisplayPrice}</p>
              ) : (
                <PriceToastDropdown locale={locale} />
              )}
              <div className="flex items-center gap-2">
                {!isOutOfStock &&
                  (!isInCart ? (
                    <AtomButton
                      variant="primary"
                      onClick={handleAddToCart}
                      disabled={isLoading || isAddingToCart}
                      size="sm"
                    >
                      {isAddingToCart ? <Loader2 className="animate-spin" /> : __("Add", locale)}
                    </AtomButton>
                  ) : (
                    <div className="flex shrink-0 items-center gap-1 text-sm">
                      <span className="flex items-center gap-1 font-medium text-green-600">
                        <ShoppingCart size={16} /> ({cartQuantity})
                      </span>
                    </div>
                  ))}
                {isRange && medusaProduct && prismicProduct && (
                  <ProductConfigurationModal
                    products={[prismicProduct]}
                    onlyIcon
                    addProductToCart={false}
                    medusaProducts={{ [medusaProduct.id]: medusaProduct }}
                    locale={locale}
                  />
                )}
              </div>
            </div>
          )}

          {/* ── Standard product ──────────────────────────────────────── */}
          {!isRange && !isPriceDynamic && (
            <dl className="w-full items-center justify-between gap-1.25">
              <div className="flex items-center justify-between gap-2">
                <dt className="sr-only" />
                {(resolvedDisplayPrice || productPrice) && (
                  <dd className="text-dark-primary w-full text-sm leading-3.5 font-semibold">
                    {isLoading ? <span className="shimmer inline-block h-4 w-12 rounded" /> : resolvedDisplayPrice}
                  </dd>
                )}
                {!isLoading && (
                  <div className="flex min-w-0 items-center gap-1">
                    {physicalPieceCount > 1 && (
                      <ProductBadge className="max-w-full whitespace-normal text-right"
                        text={formatProductQuantity(count, productConditioning, locale)}
                      />
                    )}
                    {!isOutOfStock &&
                      (!isInCart ? (
                        <AtomButton
                          variant="primary"
                          onClick={handleAddToCart}
                          disabled={isLoading || isAddingToCart}
                          size="sm"
                        >
                          {isAddingToCart ? <Loader2 className="animate-spin" /> : __("Add", locale)}
                        </AtomButton>
                      ) : (
                        <div className="flex shrink-0 items-center gap-1 text-sm">
                          <span className="flex items-center gap-1 font-medium text-green-600">
                            <ShoppingCart size={16} /> ({cartQuantity})
                          </span>
                        </div>
                      ))}
                  </div>
                )}
              </div>
              <dt className="sr-only">{__("Price per piece", locale)}</dt>
              <dd className="text-primary-light flex-1 text-xs leading-3.5 font-normal">
                {isLoading ? (
                  <span className="shimmer inline-block h-2 w-14 rounded" />
                ) : (
                  physicalPieceCount > 1 &&
                  !hasMultipleVariants && (
                    <>
                      {formatPrice(pricePerPiece, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/
                      {__("piece", locale)}
                    </>
                  )
                )}
              </dd>
            </dl>
          )}

          {isOutOfStock && (
            <Toast
              id={`inspiration-${medusaVariantId}`}
              type="info"
              className="min-[384px]:min-w-none mt-1 w-full max-w-none shadow-none max-sm:p-2"
              titleClassName="max-sm:text-[10px]"
              title={__("This product is out of stock", locale)}
            />
          )}

          {!isLoading && !isOutOfStock && (
            <div className="flex w-full items-center justify-between gap-2.5">
              <div className="flex w-full items-center gap-1.25">
                <Button
                  onClick={handleDecrease}
                  variant="outline"
                  size="icon"
                  className="border-primary-light/23 size-7.5 cursor-pointer rounded-full bg-white p-0"
                  aria-label={`${_x("Decrease quantity of {{ productName }}", { productName }, locale)}`}
                  disabled={count <= quantityStep}
                >
                  <MinusIcon size={18} />
                </Button>

                <div
                  role="status"
                  aria-live="polite"
                  aria-atomic="true"
                  className="border-primary-light/23 flex h-7.5 w-full max-w-[7.125rem] flex-1 flex-shrink-0 items-center justify-center rounded-lg border bg-white"
                >
                  <span className="text-dark-primary flex w-full items-center justify-center text-center text-base font-semibold tabular-nums">
                    {toLotQuantity(count, productConditioning)}
                  </span>
                </div>

                <Button
                  onClick={handleIncrease}
                  variant="outline"
                  size="icon"
                  className="border-primary-light/23 size-7.5 cursor-pointer rounded-full bg-white p-0"
                  aria-label={_x("Increase quantity of {{ productName }}", { productName }, locale)}
                >
                  <PlusIcon size={18} />
                </Button>
              </div>

              <button
                disabled={isRemoving}
                onClick={handleDelete}
                aria-label={_x("Remove {{ productName }} from cart", { productName }, locale)}
                className="group focus:outline-pink-primary -ml-1 size-7 rounded-full p-1 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Trash2Icon
                  size={20}
                  aria-hidden="true"
                  className="text-primary-light disabled:hover:text-primary-light group-focus:text-pink-primary hover:text-pink-primary transition-all duration-200 hover:cursor-pointer"
                />
              </button>
            </div>
          )}
        </div>
      </div>
    </article>
  );
};

export default CartProductCard;
